import type { MouseEvent } from 'react';
import { ConsentCheckbox } from '@/components/forms/ConsentCheckbox';
import { PhoneInput } from '@/components/forms/PhoneInput';
import { Select } from '@/components/forms/Select';
import { TextInput } from '@/components/forms/TextInput';
import {
  CITIES,
  SPECIALIZATION_LABELS_RU,
  SPECIALIZATIONS,
  type City,
  type Specialization,
  type UserRegion,
} from '@pomoshnik/shared';
import type { FormFieldErrors, OnboardingFormState } from './useOnboardingForm';

/**
 * Pure presenter Stage 2 регистрации (`/onboarding/register`).
 *
 * Layout (PRD §2.2 + Q9):
 *   - Шапка с логотипом.
 *   - H1 «Регистрация».
 *   - TextInput «Имя».
 *   - Select «Город» (CITIES — 20 городов, ADR-0085; смена меняет регион → phone reset).
 *   - Select «Специализация» (6 SPECIALIZATIONS из vocabulary).
 *   - PhoneInput «Телефон» (маска по region).
 *   - ConsentCheckbox dataProcessing (обязательное P-017) с inline ссылкой
 *     на `/legal/user-agreement` (placeholder Phase 2, реальные тексты —
 *     отдельная фаза `docs/legal/`). `<a onClick={e.stopPropagation()}>` —
 *     клик по ссылке не переключает чекбокс (см. Step 3 ConsentCheckbox JSDoc).
 *   - ConsentCheckbox marketingAnalytics (опциональное P-035).
 *   - Form-level error (`errors.form`) — server-side ошибка регистрации.
 *   - Submit-button — vertical, P-019.
 *
 * Без BottomBar (P-023): онбординг — entry-point, navigation back/home
 * не имеет смысла.
 */
export interface RegisterScreenViewProps {
  state: OnboardingFormState;
  errors: FormFieldErrors;
  region: UserRegion;
  submitting: boolean;
  onNameChange: (v: string) => void;
  onCityChange: (v: City) => void;
  onSpecializationChange: (v: Specialization) => void;
  onPhoneChange: (v: string) => void;
  onConsentChange: (key: 'dataProcessing' | 'marketingAnalytics', v: boolean) => void;
  onSubmit: () => void;
}

export function RegisterScreenView({
  state,
  errors,
  region,
  submitting,
  onNameChange,
  onCityChange,
  onSpecializationChange,
  onPhoneChange,
  onConsentChange,
  onSubmit,
}: RegisterScreenViewProps) {
  const cityOptions = CITIES.map((c) => ({ value: c, label: c }));
  const specOptions = SPECIALIZATIONS.map((s) => ({
    value: s,
    label: SPECIALIZATION_LABELS_RU[s],
  }));

  // Step 3 JSDoc nuance: клик по `<a>` внутри label чекбокса не должен
  // toggle'ить его. stopPropagation на якоре блокирует bubbling вверх к
  // `<label>` (нативно label обрабатывает любой клик внутри).
  const stopPropagation = (e: MouseEvent<HTMLAnchorElement>) => e.stopPropagation();

  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <div className="h-16 flex items-center px-4 bg-header border-b border-header-border">
        <span
          className="font-display text-h-logo text-ink"
          style={{ letterSpacing: '0.02em', lineHeight: 1 }}
        >
          Помощник мебельщика
        </span>
      </div>

      <section className="px-4 py-6 flex flex-col gap-5">
        <h1 className="font-display text-h1 text-ink">Регистрация</h1>

        <TextInput
          name="name"
          label="Ваше имя"
          value={state.name}
          onChange={onNameChange}
          error={errors.name}
          autoComplete="name"
          required
          maxLength={50}
          disabled={submitting}
        />

        <Select
          name="city"
          label="Ваш город"
          value={state.city}
          onChange={(v) => onCityChange(v as City)}
          options={cityOptions}
          placeholder="Выберите город"
          error={errors.city}
          required
          disabled={submitting}
        />

        <Select
          name="specialization"
          label="Специализация"
          value={state.specialization}
          onChange={(v) => onSpecializationChange(v as Specialization)}
          options={specOptions}
          placeholder="Выберите специализацию"
          error={errors.specialization}
          required
          disabled={submitting}
        />

        <PhoneInput
          name="phone"
          label="Телефон"
          region={region}
          value={state.phone}
          onChange={onPhoneChange}
          error={errors.phone}
          required
          disabled={submitting}
        />

        <ConsentCheckbox
          name="dataProcessing"
          checked={state.consents.dataProcessing}
          onChange={(v) => onConsentChange('dataProcessing', v)}
          error={errors.consents}
          required
          disabled={submitting}
        >
          Согласен на{' '}
          <a
            href="/legal/user-agreement"
            target="_blank"
            rel="noopener noreferrer"
            onClick={stopPropagation}
            className="underline text-cta"
          >
            обработку персональных данных
          </a>{' '}
          и принимаю{' '}
          <a
            href="/legal/cookie-policy"
            target="_blank"
            rel="noopener noreferrer"
            onClick={stopPropagation}
            className="underline text-cta"
          >
            политику Cookie
          </a>
        </ConsentCheckbox>

        <ConsentCheckbox
          name="marketingAnalytics"
          checked={state.consents.marketingAnalytics}
          onChange={(v) => onConsentChange('marketingAnalytics', v)}
          disabled={submitting}
        >
          Хочу получать качественный сервис и согласен на анализ использования (без личных данных)
        </ConsentCheckbox>

        {errors.form !== undefined ? (
          <p role="alert" className="text-error text-sm">
            {errors.form}
          </p>
        ) : null}

        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting}
          className={
            'min-h-tap rounded-xl bg-cta text-ink text-base font-semibold px-4 py-3 ' +
            'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none ' +
            'disabled:opacity-60 disabled:cursor-not-allowed'
          }
        >
          {submitting ? 'Регистрируем…' : 'Зарегистрироваться'}
        </button>
      </section>
    </main>
  );
}
